import { useState, useEffect, useCallback, useRef } from "react";
import { questionApi, QuestionItem } from "@/services/api/question";
// 跨实例防重复请求：基于参数的进行中请求表
const globalInFlightRequests: Map<
  string,
  Promise<{
    list: QuestionItem[];
    total: number;
    page: number;
    pageSize: number;
  }>
> = new Map();

function buildRequestKey(
  params: UseQuestionListParams,
  page: number,
  pageSize: number
): string {
  const { brandId, categoryId, days, metricType, modelId } = params;
  return JSON.stringify({
    brandId,
    categoryId,
    days: days ?? null,
    metricType,
    modelId: modelId ?? "all",
    page,
    pageSize,
  });
}

export interface UseQuestionListParams {
  brandId: string;
  categoryId: string;
  days?: number;
  metricType: string;
  modelId?: string;
  page?: number;
  pageSize?: number;
}

export interface UseQuestionListResult {
  questions: QuestionItem[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  refresh: () => Promise<void>;
  loadMore: () => Promise<void>;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
}

export const useQuestionList = (
  params: UseQuestionListParams
): UseQuestionListResult => {
  const [questions, setQuestions] = useState<QuestionItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(params.page || 1);
  const [pageSize, setPageSize] = useState(params.pageSize || 20);

  // 防重复请求的引用
  const inFlightRef = useRef<boolean>(false);
  // 记录上一次的 metricType，用于变更检测
  const prevMetricTypeRef = useRef<string>(params.metricType);
  // 参数切换时用于跳过下一轮自动请求
  const skipNextAutoRef = useRef<boolean>(false);
  // 回滚：不使用版本号失效机制

  const fetchQuestions = useCallback(
    async (isLoadMore = false, targetPage?: number) => {
      const currentPage = targetPage || page;

      // 防重复请求
      if (inFlightRef.current) {
        console.log("🚫 请求进行中，跳过重复请求");
        return;
      }

      console.log("🔍 useQuestionList 参数检查:", {
        brandId: params.brandId,
        categoryId: params.categoryId,
        metricType: params.metricType,
        days: params.days,
        modelId: params.modelId,
        page: currentPage,
        pageSize,
        isLoadMore,
      });

      if (!params.brandId || !params.categoryId || !params.metricType) {
        console.log("❌ 参数不完整，跳过请求");
        return;
      }

      console.log("✅ 参数完整，开始请求问题列表");

      inFlightRef.current = true;

      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      try {
        const key = buildRequestKey(params, currentPage, 10);
        let request = globalInFlightRequests.get(key);
        if (!request) {
          request = questionApi.getQuestionList({
            brandId: params.brandId,
            categoryId: params.categoryId,
            days: params.days,
            metricType: params.metricType,
            modelId: params.modelId ?? "all",
            page: currentPage,
            pageSize: 15,
          });
          globalInFlightRequests.set(key, request);
        }
        const result = await request;
        globalInFlightRequests.delete(key);

        if (isLoadMore) {
          // 加载更多：追加到现有列表
          setQuestions((prev) => [...prev, ...result.list]);
        } else {
          // 初始加载：替换列表
          setQuestions(result.list);
        }
        setTotal(result.total);
      } catch (err: any) {
        console.error("获取问题列表失败:", err);
        setError(err.message || "获取问题列表失败");
      } finally {
        inFlightRef.current = false;
        if (isLoadMore) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      params.brandId,
      params.categoryId,
      params.days,
      params.metricType,
      params.modelId,
      page,
      pageSize,
    ]
  );

  const refresh = useCallback(async () => {
    setPage(1);
    await fetchQuestions(false);
  }, [fetchQuestions]);

  const loadMore = useCallback(async () => {
    if (loadingMore || questions.length >= total) return;

    console.log("📜 开始加载更多数据...");
    const nextPage = page + 1;
    setPage(nextPage);
    // 直接调用 fetchQuestions，传入正确的 page 参数
    await fetchQuestions(true, nextPage);
  }, [fetchQuestions, loadingMore, questions.length, total, page]);

  const handleSetPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSetPageSize = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // 重置到第一页
  }, []);

  // 计算是否还有更多数据
  const hasMore = questions.length < total;

  // 监听 metricType 变化（仅做检测与日志）
  useEffect(() => {
    if (prevMetricTypeRef.current !== params.metricType) {
      console.log("[useQuestionList] metricType 变化:", {
        from: prevMetricTypeRef.current,
        to: params.metricType,
      });
      prevMetricTypeRef.current = params.metricType;
    }
  }, [params.metricType]);

  // 关键参数变化时：清空并重置页码，然后立即以 page=1 发起请求
  useEffect(() => {
    setQuestions([]);
    setTotal(0);
    setError(null);
    setPage(1);
    // 重置加载标记，确保从0开始
    setLoading(false);
    setLoadingMore(false);
    // 立即以第一页请求新数据，并跳过下一轮自动 effect 的请求
    skipNextAutoRef.current = true;
    (async () => {
      await fetchQuestions(false, 1);
    })();
  }, [
    params.brandId,
    params.categoryId,
    params.days,
    params.metricType,
    params.modelId,
  ]);

  // 当参数变化时自动刷新
  useEffect(() => {
    if (skipNextAutoRef.current) {
      // 跳过本次自动请求，避免与参数切换时的手动请求重复
      skipNextAutoRef.current = false;
      return;
    }
    fetchQuestions();
  }, [fetchQuestions]);

  return {
    questions,
    loading,
    loadingMore,
    error,
    total,
    page,
    pageSize,
    hasMore,
    refresh,
    loadMore,
    setPage: handleSetPage,
    setPageSize: handleSetPageSize,
  };
};

export default useQuestionList;
